<script lang="ts" module>
  type Sticker = {
    image: {
      src: string;
      alt: string;
    };
    name: string;
    type?: string;
    formattedSize: string;
  };

  type CustomSticker = {
    customStickerId: string;
    customStickerVersion: string;
    name: string;
    type?: string;
    formattedSize: string;
  };

  export type AddedItem = Sticker | CustomSticker;

  export const getAddedCustomSticker = (
    customSticker: CustomStickerWithId,
    productNameTranslator: (key: string) => string,
    customStickerTranslation: string,
    locale: LocalizationContext,
  ): CustomSticker => ({
    customStickerId: customSticker.id,
    customStickerVersion: customSticker.version,
    name: productNameTranslator(customSticker.productItem.productName),
    type: customStickerTranslation,
    formattedSize: formatSize(
      customSticker.attributes.width as number,
      customSticker.attributes.height as number,
      locale,
      true,
    ),
  });
</script>

<script lang="ts">
  import { ButtonLink } from '@athena-lib/Button';

  import type { BuyStickerCardProps } from '@athena-lib/BuyStickerCard';
  import BuyStickerCard from '@athena-lib/BuyStickerCard/BuyStickerCard.svelte';
  import type { CheckoutSdk } from '@athena-lib/Checkout/utils';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Grid, GridItem } from '@athena-lib/Grid';
  import type { LocalizationContext } from '@athena-lib/i18n';
  import Image from '@athena-lib/Image/Image.svelte';
  import { LiveLineItemStickerPreview } from '@athena-lib/LivePreview';
  import { formatSize } from '@athena-lib/ProductWizard';
  import { Text } from '@athena-lib/Text';
  import HeadingMedium from '@athena-lib/Text/HeadingMedium/HeadingMedium.svelte';
  import HeadingSmall from '@athena-lib/Text/HeadingSmall/HeadingSmall.svelte';
  import type { CustomStickerWithId } from '@stickerapp-org/checkout-sdk';

  const {
    addedItem,
    upsellItems,
    getCustomSticker,
    onClickCartLink,
    reviewCartUrl,
    onButtonClick,
    checkoutUrl,
  }: {
    addedItem: AddedItem;
    getCustomSticker: CheckoutSdk['getCustomSticker'];
    upsellItems: Array<BuyStickerCardProps>;
    onClickCartLink?: () => void;
    reviewCartUrl?: string;
    onButtonClick?: () => void;
    checkoutUrl?: string;
  } = $props();

  const commonTranslations = getCommonTranslations();
</script>

<div class="flex h-full flex-col px-5 pb-5 pt-14 text-center">
  <HeadingMedium size="small" class="text-center">{commonTranslations.youHaveAdded}</HeadingMedium>
  <div class="mt-4 flex flex-col items-center gap-1 text-center">
    {#if 'customStickerId' in addedItem}
      <LiveLineItemStickerPreview
        class="max-h-96"
        customStickerPromise={getCustomSticker(
          addedItem.customStickerId,
          addedItem.customStickerVersion,
        )}
      />
    {:else}
      <Image
        src={addedItem.image.src}
        alt={addedItem.image.alt}
        class="aspect-auto max-h-full max-w-full"
      />
    {/if}

    <Text bold>{addedItem.name}</Text>
    {#if addedItem.type}
      <Text size="small">{addedItem.type}</Text>
    {/if}
    <Text size="small">{addedItem.formattedSize}</Text>
  </div>
  {#if upsellItems.length > 0}
    <hr />
    <HeadingSmall size="xxsmall">{commonTranslations.weAlsoRecommend}</HeadingSmall>
    <Grid space="gutter" columns={2}>
      {#each upsellItems as upsellItem}
        <GridItem>
          <BuyStickerCard {...upsellItem} small />
        </GridItem>
      {/each}
    </Grid>
  {/if}
  {#if onClickCartLink}
    <div class="mt-auto flex justify-center">
      <div class="flex w-full flex-row place-items-center gap-4">
        <a
          href={reviewCartUrl}
          color="dark"
          class="w-1/2 basis-auto text-center align-middle font-bold underline"
          onclick={onButtonClick}
        >
          {commonTranslations.reviewCart}
        </a>
        <ButtonLink class="basis-auto" link={{ href: checkoutUrl }} onClick={onButtonClick}
          >{commonTranslations.checkoutNow}</ButtonLink
        >
      </div>
    </div>
  {/if}
</div>
